import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import ArrowRightIcon from '@/assets/icons/arrow-down-icon.svg?react';
import FacebookMessengerIcon from '@/assets/icons/facebook-messenger.svg?react';
import InstagramIcon from '@/assets/icons/instagram.svg?react';
import LineIcon from '@/assets/icons/line.svg?react';
import SleekFlowLogo from '@/assets/icons/sleekflow.svg?react';
import TelegramIcon from '@/assets/icons/telegram.svg?react';
import ViberIcon from '@/assets/icons/viber.svg?react';
import WechatIcon from '@/assets/icons/wechat.svg?react';
import WhatsappIcon from '@/assets/icons/whatsapp.svg?react';
import useChannels from '@/hooks/useChannels';
import { useWidgetConfig } from '@/hooks/useWidgetConfig';

interface FooterProps {
  onChannelSelect?: (channel: string) => void;
}

const ICON_MAP = {
  whatsapp: <WhatsappIcon width="24" height="24" title="WhatsApp" />,
  facebook: (
    <FacebookMessengerIcon width="24" height="24" title="Facebook Messenger" />
  ),
  instagram: <InstagramIcon width="24" height="24" title="Instagram" />,
  wechat: <WechatIcon width="24" height="24" title="WeChat" />,
  line: <LineIcon width="24" height="24" title="Line" />,
  telegram: <TelegramIcon width="24" height="24" title="Telegram" />,
  viber: <ViberIcon width="24" height="24" title="Viber" />,
};

const Footer = ({ onChannelSelect }: FooterProps) => {
  const { t } = useTranslation();
  const [showChannelSelection, setShowChannelSelection] = useState(false);
  const {
    channelConfig: { availableChannels },
  } = useWidgetConfig();

  const handleToggleChannelSelection = useCallback(
    () => setShowChannelSelection(!showChannelSelection),
    [showChannelSelection],
  );

  const handleChannelClick = useCallback(
    (channel: string) => {
      if (onChannelSelect) {
        onChannelSelect(channel);
      }
      setShowChannelSelection(false);
    },
    [onChannelSelect],
  );

  const { isOtherChannelsAvailable } = useChannels();
  const isShowingChannelSelectionButton = useMemo(
    () => isOtherChannelsAvailable && onChannelSelect,
    [isOtherChannelsAvailable, onChannelSelect],
  );

  return (
    <div className="sf:min-h-6 sf:relative">
      {/* Channel Selection Bar */}
      {showChannelSelection &&
        isShowingChannelSelectionButton &&
        availableChannels && (
          <div className="sf:absolute sf:bottom-full sf:right-2 sf:p-1 sf:flex sf:flex-row sf:bg-white/60 sf:rounded-xl">
            {Object.entries(availableChannels)
              .filter(([channel, value]) => value && channel !== 'livechat')
              .map(([channel]) => (
                <div
                  key={channel}
                  className="sf:p-1 sf:cursor-pointer sf:hover:bg-gray-10 sf:rounded-md"
                  onClick={() => handleChannelClick(channel)}
                >
                  {ICON_MAP[channel as keyof typeof ICON_MAP]}
                </div>
              ))}
          </div>
        )}

      {/* Footer Content */}
      <div
        className={classNames({
          'sf:flex sf:items-center sf:py-1 sf:px-4': true,
          'sf:justify-center': !isShowingChannelSelectionButton,
          'sf:justify-between': isShowingChannelSelectionButton,
        })}
      >
        <div
          className="sf:flex sf:items-center sf:gap-1 sf:cursor-pointer"
          onClick={() => {
            window.open(
              'https://sleekflow.io/channels/live-chat?utm_source=widget',
              '_blank',
              'noopener,noreferrer',
            );
          }}
        >
          <span className="sf:text-body3">
            {t('channelDetail.common.poweredBy', 'Powered by')}
          </span>
          <SleekFlowLogo className="sf:w-3 sf:h-3" />
          <span className="sf:text-body3 sf:text-gray-900">
            {t('channelDetail.common.sleekflow', 'SleekFlow')}
          </span>
        </div>
        {isShowingChannelSelectionButton && (
          <button
            className="sf:flex sf:items-center sf:gap-1 sf:px-2 sf:py-1 sf:rounded sf:cursor-pointer sf:z-50"
            onClick={handleToggleChannelSelection}
          >
            <span className="sf:text-body3">
              {t('chatWindow.contactOtherChannels')}
            </span>
            <ArrowRightIcon
              className={classNames(
                'sf:w-3 sf:h-3 sf:text-[#3C3F44] sf:transition-transform',
                { 'sf:rotate-180': showChannelSelection },
              )}
            />
          </button>
        )}
      </div>
    </div>
  );
};

export default Footer;
